import { filter, map } from 'lodash'
import { useCallback, useState } from 'react'
import { useEffectOnce } from 'react-use'

import { useErrorNotification, useRemoteCollection, useSuccessNotification } from 'civic-champs-shared/api/hooks'
import { BulkAction } from 'civic-champs-shared/api/hooks/useRemoteCollection'
import { useShowPrompt } from 'civic-champs-shared/core/modal/hooks'
import useGetCheckIns from './useGetCheckIns'
import useDeleteCheckIns from './useDeleteCheckIns'
import { CheckIn } from '../types'
import { BulkCheckOutPrompt, BulkCheckOutResult as CheckOutResult } from '../components/BulkCheckOutPrompt'
import { useTimesheetCheckOut } from './useTimesheetCheckOut'

export interface CheckInCollectionData {
  checkIns: CheckIn[]
  loading: boolean
  busy: boolean
}

export interface BulkCheckOutResult {
  success: boolean
}

export interface CheckInCollectionOperations {
  refresh: () => Promise<void>
  bulkCheckOut: (checkIns: CheckIn[]) => Promise<BulkCheckOutResult>
  bulkDelete: (checkIns: CheckIn[]) => Promise<BulkCheckOutResult>
}

const useBulkCheckOut = (eagerRemoveMany: BulkAction<CheckIn>) => {
  const showPrompt = useShowPrompt(BulkCheckOutPrompt)
  const showSuccess = useSuccessNotification()
  const showError = useErrorNotification()

  const [checkingOut, setCheckingOut] = useState<boolean>(false)
  const [adminCheckOut] = useTimesheetCheckOut()

  const bulkCheckOut = useCallback(
    async (checkIns: CheckIn[]) => {
      setCheckingOut(true)
      try {
        const checkInIds = map(checkIns, checkIn => checkIn.id)
        const result = (await showPrompt({ checkInIds })) as CheckOutResult

        if (!result.completed) return { success: false }

        const ids = new Set<number>(result.checkOuts.map(checkOut => checkOut.activeVolunteerId))
        const finalCheckIns = filter(checkIns, checkIn => ids.has(checkIn.id))

        const undo = eagerRemoveMany(finalCheckIns)
        try {
          await adminCheckOut(result.checkOuts, checkIns)
          showSuccess('Check-out complete!')
          return { success: true }
        } catch (error) {
          undo()
          showError('Check-out failed', error)
          return { success: false }
        }
      } finally {
        setCheckingOut(false)
      }
    },
    [adminCheckOut, eagerRemoveMany, showError, showPrompt, showSuccess],
  )

  return { bulkCheckOut, checkingOut }
}

const useBulkDelete = (eagerRemoveMany: BulkAction<CheckIn>): [(checkIns: CheckIn[]) => Promise<BulkCheckOutResult>, boolean] => {
  const [deleteCheckIns, deleting] = useDeleteCheckIns()
  const showError = useErrorNotification()
  const showSuccess = useSuccessNotification()

  const bulkDelete = useCallback(
    async (checkIns: CheckIn[]) => {
      const undo = eagerRemoveMany(checkIns)
      try {
        const ids = map(checkIns, checkIn => checkIn.id)
        await deleteCheckIns(ids)
        showSuccess('Check-ins deleted')
        return { success: true }
      } catch (err) {
        undo()
        showError('failed to delete check-ins', err)
        return { success: false }
      }
    },
    [deleteCheckIns, showError, showSuccess, eagerRemoveMany],
  )

  return [bulkDelete, deleting]
}

const useCheckInsCollection = (): [CheckInCollectionData, CheckInCollectionOperations] => {
  const [checkIns, operations] = useRemoteCollection<CheckIn>()
  const [getCheckIns, { called, loading }] = useGetCheckIns()
  const [bulkDelete, deleting] = useBulkDelete(operations.eagerRemoveMany)

  const { syncCollection, eagerRemoveMany } = operations
  const refresh = useCallback(async () => {
    const updatedCheckIns = await getCheckIns()
    syncCollection(updatedCheckIns)
  }, [getCheckIns, syncCollection])

  useEffectOnce(() => {
    refresh()
  })

  const { bulkCheckOut, checkingOut } = useBulkCheckOut(eagerRemoveMany)

  return [
    {
      checkIns,
      loading: !called || loading,
      busy: loading || checkingOut || deleting,
    },
    {
      refresh,
      bulkCheckOut,
      bulkDelete,
    },
  ]
}

export default useCheckInsCollection
